import React from 'react';
import { Routes, Navigate, Route } from 'react-router-dom';

import { RouteWithLayout } from './components';
import { Main as MainLayout, Minimal as MinimalLayout, PrintLayout } from './layouts';

import {
  ROLES_MANAGEMENT_ALL,
  ROLES_MANAGEMENT_BASIC,
  ROLE_ADMIN,
  ROLE_MENU_CUSTOMERS,
  ROLE_MENU_EXPENSES,
  ROLE_MENU_PRODUCTS,
  ROLE_MENU_SERVICES,
} from 'commons/constants';
import NotFoundView from './views/NotFound/NotFound';
import PlaygroundPage from './views/playground/PlaygroundPage';
import ComponentPortfolioPage from './views/playground/ComponentPortfolioPage';
import AppLoadingIndicator from './AppLoadingIndicator';
import UnifyCustomerAutomaticPage from 'views/customers/unify/UnifyCustomerAutomaticPage';
import UnifyCustomerManualPage from 'views/customers/unify/UnifyCustomerManualPage';
import UnifyCustomerPage from 'views/customers/unify/UnifyCustomerPage';
import CustomerCreditsListPage from 'views/customers/customer-credits/CustomerCreditsListPage';
import AccountAndSettingsBasePage from 'views/accountAndSettings/AccountAndSettingsBasePage';
import CourtListPage from 'views/accountAndSettings/court-list/CourtListPage';
import ServiceCategoryHomePage from 'views/services/category/ServiceCategoryHomePage';

const ROLES_ADMIN = [ROLE_ADMIN];

const DEV_ONLY_ROUTES =
  process.env.NODE_ENV === 'production'
    ? []
    : [
        <Route key={0} element={<MainLayout />}>
          <Route
            key={0}
            path="playground"
            element={<RouteWithLayout component={PlaygroundPage} privateRoute requiresPremiumAcc />}
          />
          ,
          <Route
            path="components"
            key={1}
            element={<RouteWithLayout component={ComponentPortfolioPage} privateRoute requiresPremiumAcc />}
          />
        </Route>,
      ];

// LAZY ROUTES
const AgendaPage = React.lazy(() => import('./views/agenda/AgendaPage'));
const AgendaBasePage = React.lazy(() => import('./views/agenda/AgendaBasePage'));
const ChargesListPage = React.lazy(() => import('./views/chargesManagement/ChargesListPage'));
const ComandasListPage = React.lazy(() => import('./views/chargeNotes/comandas/ComandasListPage'));
const PredefinedMessageListPage = React.lazy(() =>
  import('./views/predefinedMsgs/PredefinedMessageListPage'),
);
const PredefinedMessageBasePage = React.lazy(() =>
  import('./views/predefinedMsgs/PredefinedMessageBasePage'),
);
const PredefinedMessageFormPage = React.lazy(() =>
  import('./views/predefinedMsgs/PredefinedMessageFormPage'),
);
const CustomersAndAnamnesesBasePage = React.lazy(() =>
  import('./views/customers-anamneses/CustomersAndAnamnesesBasePage'),
);
const CustomerList = React.lazy(() => import('./views/customers/home/CustomerList'));
const CustomerBirthDayList = React.lazy(() => import('./views/customers/birthday/CustomerBirthDayList'));
const CustomersGoneList = React.lazy(() => import('./views/customers/customers-gone/CustomersGoneList'));
const CustomerFormPage = React.lazy(() => import('./views/customers/CustomerFormPage'));
const CustomersReturningList = React.lazy(() =>
  import('./views/customers/customers-returning/CustomersReturningList'),
);
const CustomerDetailsPage = React.lazy(() =>
  import('./views/customers/customer-details/CustomerDetailsPage'),
);
const CustomerAppointmentsByTagPage = React.lazy(() =>
  import('./views/customers/CustomerAppointmentsByTagPage'),
);
const CustomerCommentsListPage = React.lazy(() =>
  import('./views/customers/customer-comments/CustomerCommentsListPage'),
);
const CustomerImages = React.lazy(() => import('./views/customers/customer-images/CustomerImages'));
const CustomerBudgetListPage = React.lazy(() =>
  import('./views/customers/customer-budgets/CustomerBudgetListPage'),
);
const CustomerBudgetFormPage = React.lazy(() =>
  import('./views/customers/customer-budgets/CustomerBudgetFormPage'),
);
const AnamneseBasic = React.lazy(() => import('./views/anamnese/AnamneseBasic'));

const EmployeesAndComissionsBasePage = React.lazy(() =>
  import('./views/employees-comissions/EmployeesAndComissionsBasePage'),
);
const EmployeesListPage = React.lazy(() => import('./views/employees/EmployeesListPage'));
const ServiceListPage = React.lazy(() => import('./views/services/ServiceListPage'));
const ServiceFormPage = React.lazy(() => import('./views/services/ServiceFormPage'));
const ServicesAndPackagesBasePage = React.lazy(() =>
  import('./views/service-packages/ServicesAndPackagesBasePage'),
);
const ServicePackageListPage = React.lazy(() => import('./views/service-packages/ServicePackageListPage'));
const ServicePackageFormPage = React.lazy(() => import('./views/service-packages/ServicePackageFormPage'));
const ExpensesBasePage = React.lazy(() => import('./views/expenses/ExpensesBasePage'));
const ExtraRevenueBasePage = React.lazy(() => import('./views/extraRevenues/ExtraRevenueBasePage'));
const ExpenseListPage = React.lazy(() => import('./views/expenses/ExpenseListPage'));
const ExpenseFormPage = React.lazy(() => import('./views/expenses/ExpenseFormPage'));
const ExpenseCategoryHomePage = React.lazy(() => import('./views/expenses/category/ExpenseCategoryHomePage'));
const ExtraRevenueListPage = React.lazy(() => import('./views/extraRevenues/ExtraRevenueListPage'));
const ExtraRevenueFormPage = React.lazy(() => import('./views/extraRevenues/ExtraRevenueFormPage'));
const ExtraRevenueCategoryHomePage = React.lazy(() =>
  import('./views/extraRevenues/category/ExtraRevenueCategoryHomePage'),
);
const ProductCategoryHomePage = React.lazy(() => import('./views/sales/category/ProductCategoryHomePage'));
const ProductSupplierHomePage = React.lazy(() => import('./views/sales/supplier/ProductSupplierHomePage'));
const ProductStockHome = React.lazy(() => import('./views/sales/productStock/ProductStockHome'));
const ProductsAndStockBasePage = React.lazy(() => import('./views/sales/ProductsAndStockBasePage'));
const ProductPurchasesHome = React.lazy(() => import('./views/sales/productPurchases/ProductPurchasesHome'));
const ProductSalesHome = React.lazy(() => import('./views/sales/productSales/ProductSalesHome'));
const PerformanceBasePage = React.lazy(() => import('./views/performance/PerformanceBasePage'));
const PerformanceCompany = React.lazy(() => import('./views/performance/company-tab/PerformanceCompany'));
const PerformanceEmployees = React.lazy(() =>
  import('./views/performance/employees-tab/PerformanceEmployees'),
);
const BestCustomersPage = React.lazy(() => import('./views/best-customers/BestCustomersPage'));
const ReportsPage = React.lazy(() => import('./views/reports/ReportsPage'));
const ReportsBasePage = React.lazy(() => import('./views/reports/ReportsBasePage'));
const AccountAndSettingsPage = React.lazy(() => import('./views/accountAndSettings/AccountAndSettingsPage'));
const SignInView = React.lazy(() => import('./views/SignIn/SignIn'));
const ResetPasswordPage = React.lazy(() => import('./views/SignIn/ResetPasswordPage'));
const SetPasswordPage = React.lazy(() => import('./views/SignIn/SetPasswordPage'));
const CommissionsPage = React.lazy(() => import('./views/commissions/CommissionsPage'));
const CommissionPaymentPrintPage = React.lazy(() => import('./views/commissions/CommissionPaymentPrintPage'));
const AnamnesePage = React.lazy(() => import('./views/anamnese/AnamnesePage'));

const NewUsersPage = React.lazy(() => import('./views/rootUser/NewUsersPage'));
const CompaniesExpiringPage = React.lazy(() => import('./views/rootUser/CompaniesExpiringPage'));
const ManageCompanyPage = React.lazy(() => import('./views/rootUser/ManageCompanyPage'));
const ManageReleaseNewsPage = React.lazy(() =>
  import('./views/rootUser/manageReleaseNews/ManageReleaseNewsPage'),
);
const ManageUserPage = React.lazy(() => import('./views/rootUser/ManageUserPage'));
const AsaasCustomersPage = React.lazy(() => import('./views/rootUser/AsaasCustomersPage'));

const PaypalGetPremiumView = React.lazy(() => import('./views/premium/PaypalGetPremiumView'));
const StripeGetPremiumView = React.lazy(() => import('./views/premium/StripeGetPremiumView'));
const StripeIamPremiumView = React.lazy(() => import('./views/premium/StripeIamPremiumView'));

const StripePortalWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/StripePortalWithUserCodeView'),
);

const StripePremiumWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/StripePremiumWithUserCodeView'),
);
const StripePremiumSuccessWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/StripePremiumSuccessWithUserCodeView'),
);
const AsaasPremiumWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/AsaasPremiumWithUserCodeView'),
);
const AsaasAtivarWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/asaas-activate/AsaasAtivarWithUserCodeView'),
);
const StripeUpgradeWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/StripeUpgradeWithUserCodeView'),
);
const StripeUpgradeSuccessWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/StripeUpgradeSuccessWithUserCodeView'),
);

const StripeChangeDueDateWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/StripeChangeDueDateWithUserCodeView'),
);
const StripeChangeDueDateSuccessWithUserCodeView = React.lazy(() =>
  import('./views/premium/premium-temp-code/StripeChangeDueDateSuccessWithUserCodeView'),
);

const ManageStripeSubscriptionView = React.lazy(() => import('./views/premium/ManageStripeSubscriptionView'));
// LAZY ROUTES

const ROLES_MANAGEMENT_BASIC_MENU_CUSTOMERS = [...ROLES_MANAGEMENT_BASIC, ROLE_MENU_CUSTOMERS];
const ROLES_MANAGEMENT_ALL_MENU_SERVICES = [...ROLES_MANAGEMENT_ALL, ROLE_MENU_SERVICES];
const ROLES_MANAGEMENT_ALL_MENU_PRODUCTS = [...ROLES_MANAGEMENT_ALL, ROLE_MENU_PRODUCTS];
const ROLES_MANAGEMENT_ALL_MENU_EXPENSES = [...ROLES_MANAGEMENT_ALL, ROLE_MENU_EXPENSES];

const AppRoutes = () => {
  return (
    <React.Suspense fallback={<AppLoadingIndicator />}>
      <Routes>
        <Route path="/" element={<Navigate to="/agenda" />} />
        {DEV_ONLY_ROUTES}

        {/* BEGIN - Routes with MainLayout */}
        <Route element={<MainLayout />}>
          <Route
            path="agenda"
            element={<RouteWithLayout component={AgendaBasePage} privateRoute requiresPremiumAcc />}
          >
            <Route index element={<AgendaPage />} />
            <Route path="editar-cliente/:id" element={<CustomerFormPage />} />
            <Route path="anotacoes/:customerId" element={<CustomerCommentsListPage />} />
          </Route>

          <Route
            path="cobrancas"
            element={
              <RouteWithLayout
                component={ChargesListPage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_MANAGEMENT_BASIC}
              />
            }
          />
          <Route
            path="comandas"
            element={
              <RouteWithLayout
                component={ComandasListPage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_MANAGEMENT_BASIC}
              />
            }
          />

          <Route
            path="detalhes-do-cliente/:customerId"
            element={<RouteWithLayout component={CustomerDetailsPage} privateRoute requiresPremiumAcc />}
          />

          <Route
            path="msgs-pre-definidas"
            element={
              <RouteWithLayout
                component={PredefinedMessageBasePage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_MANAGEMENT_BASIC}
              />
            }
          >
            <Route index element={<PredefinedMessageListPage />} />
            <Route path="novo" element={<PredefinedMessageFormPage />} />
            <Route path="editar/:id" element={<PredefinedMessageFormPage />} />
          </Route>

          <Route
            path="quadras"
            element={
              <RouteWithLayout
                component={CourtListPage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_MANAGEMENT_BASIC}
              />
            }
          />

          <Route
            path="profissionais"
            element={
              <RouteWithLayout
                component={EmployeesAndComissionsBasePage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_ADMIN}
              />
            }
          >
            <Route index element={<EmployeesListPage />} />
            <Route path="comissoes" element={<CommissionsPage />} />
          </Route>

          <Route
            path="clientes"
            element={
              <RouteWithLayout
                component={CustomersAndAnamnesesBasePage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_MANAGEMENT_BASIC_MENU_CUSTOMERS}
              />
            }
          >
            <Route index element={<CustomerList />} />
            <Route path="sumidos" element={<CustomersGoneList />} />
            <Route path="retornos" element={<CustomersReturningList />} />
            <Route path="aniversariantes" element={<CustomerBirthDayList />} />
            <Route path="unificar/automatica" element={<UnifyCustomerAutomaticPage />} />
            <Route path="unificar/manual" element={<UnifyCustomerManualPage />} />
            <Route path="unificar" element={<UnifyCustomerPage />} />
            <Route path="editar/:id" element={<CustomerFormPage />} />
            <Route path="novo" element={<CustomerFormPage />} />
            <Route path="detalhe/:customerId" element={<CustomerDetailsPage />} />
            <Route path="cancelamentos/:customerId" element={<CustomerAppointmentsByTagPage tagCanceled />} />
            <Route path="nao-comparecimentos/:customerId" element={<CustomerAppointmentsByTagPage />} />
            <Route path="anotacoes/:customerId" element={<CustomerCommentsListPage />} />
            <Route path="creditos/:customerId" element={<CustomerCreditsListPage />} />
            <Route path="anamneses/:customerId" element={<AnamneseBasic />} />
            <Route path="images/:customerId" element={<CustomerImages />} />
            <Route path="orcamentos/:customerId" element={<CustomerBudgetListPage />} />
            <Route path="orcamentos/editar/:id" element={<CustomerBudgetFormPage />} />
            <Route path="orcamentos/novo" element={<CustomerBudgetFormPage />} />

            <Route
              path="fichas-anamneses/*"
              element={
                <RouteWithLayout
                  component={AnamnesePage}
                  privateRoute
                  requiresPremiumAcc
                  requiredRoles={ROLES_MANAGEMENT_BASIC_MENU_CUSTOMERS}
                />
              }
            />
          </Route>

          <Route
            path="servicos"
            element={
              <RouteWithLayout
                component={ServicesAndPackagesBasePage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_MANAGEMENT_ALL_MENU_SERVICES}
              />
            }
          >
            <Route index element={<ServiceListPage />} />
            <Route path="pacotes" element={<ServicePackageListPage />} />
            <Route path="servico/:id" element={<ServiceFormPage />} />
            <Route path="servico" element={<ServiceFormPage />} />
            <Route path="pacote/:id" element={<ServicePackageFormPage />} />
            <Route path="pacote" element={<ServicePackageFormPage />} />
            <Route path="categorias" element={<ServiceCategoryHomePage />} />
          </Route>

          <Route
            path="despesas"
            element={
              <RouteWithLayout
                component={ExpensesBasePage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_MANAGEMENT_ALL_MENU_EXPENSES}
              />
            }
          >
            <Route index element={<ExpenseListPage />} />
            <Route path="editar/:id" element={<ExpenseFormPage />} />
            <Route path="novo" element={<ExpenseFormPage />} />
            <Route path="categorias" element={<ExpenseCategoryHomePage />} />
          </Route>

          <Route
            path="produtos-estoque"
            element={
              <RouteWithLayout
                component={ProductsAndStockBasePage}
                privateRoute
                requiredRoles={ROLES_MANAGEMENT_ALL_MENU_PRODUCTS}
              />
            }
          >
            <Route index element={<ProductStockHome />} />
            <Route path="compra-estoque" element={<ProductPurchasesHome />} />
            <Route path="venda" element={<ProductSalesHome />} />
            <Route path="categorias" element={<ProductCategoryHomePage />} />
            <Route path="fornecedores" element={<ProductSupplierHomePage />} />
          </Route>

          <Route
            path="performance"
            element={
              <RouteWithLayout component={PerformanceBasePage} privateRoute requiredRoles={ROLES_ADMIN} />
            }
          >
            <Route index element={<PerformanceCompany />} />
            <Route path="profissionais" element={<PerformanceEmployees />} />
          </Route>

          <Route
            path="relatorios"
            element={
              <RouteWithLayout
                component={ReportsBasePage}
                privateRoute
                requiresPremiumAcc
                requiredRoles={ROLES_ADMIN}
              />
            }
          >
            <Route index element={<ReportsPage />} />
            {/* receitas avulsas */}
            <Route path="receitas-avulsas" element={<RouteWithLayout component={ExtraRevenueBasePage} />}>
              <Route index element={<ExtraRevenueListPage />} />
              <Route path="editar/:id" element={<ExtraRevenueFormPage />} />
              <Route path="novo" element={<ExtraRevenueFormPage />} />
              <Route path="categorias" element={<ExtraRevenueCategoryHomePage />} />
            </Route>
          </Route>

          <Route
            path="melhores-clientes"
            element={
              <RouteWithLayout component={BestCustomersPage} privateRoute requiredRoles={ROLES_ADMIN} />
            }
          />

          {/* <Route
            path="configuracoes"
            element={<RouteWithLayout component={AccountAndSettingsPage} privateRoute />}
          /> */}

          <Route
            path="configuracoes"
            element={<RouteWithLayout component={AccountAndSettingsBasePage} privateRoute />}
          >
            <Route index element={<AccountAndSettingsPage />} />
          </Route>

          <Route path="newUsers" element={<RouteWithLayout component={NewUsersPage} privateRoute />} />

          <Route
            path="companiesExpiring"
            element={<RouteWithLayout component={CompaniesExpiringPage} privateRoute />}
          />

          <Route
            path="manageCompany"
            element={<RouteWithLayout component={ManageCompanyPage} privateRoute />}
          />

          <Route
            path="manageReleaseNews"
            element={<RouteWithLayout component={ManageReleaseNewsPage} privateRoute />}
          />

          <Route path="manageUser" element={<RouteWithLayout component={ManageUserPage} privateRoute />} />

          <Route
            path="asaasCustomers"
            element={<RouteWithLayout component={AsaasCustomersPage} privateRoute />}
          />
        </Route>
        {/* END - Routes with MainLayout */}

        {/* BEGIN - MinimalLayout with showLoginBtn={false} with showUserWelcomeMsg */}
        <Route element={<MinimalLayout showLoginBtn={false} showUserWelcomeMsg />}>
          <Route
            path="obter-premium"
            element={<RouteWithLayout component={PaypalGetPremiumView} privateRoute />}
          />
          <Route
            path="quero-ser-premium"
            element={<RouteWithLayout component={StripeGetPremiumView} privateRoute />}
          />
          <Route
            path="black-friday"
            element={<RouteWithLayout component={StripeGetPremiumView} privateRoute />}
          />
          <Route
            path="sou-premium"
            element={<RouteWithLayout component={StripeIamPremiumView} privateRoute />}
          />
          <Route
            path="gerenciar-assinatura"
            element={<RouteWithLayout component={ManageStripeSubscriptionView} privateRoute />}
          />
        </Route>
        {/* END - MinimalLayout com showLoginBtn={false} e showUserWelcomeMsg */}

        {/* BEGIN - Routes with MinimalLayout */}
        <Route element={<MinimalLayout />}>
          <Route path="not-found" element={<RouteWithLayout component={NotFoundView} />} />
          <Route
            path="login"
            element={<RouteWithLayout component={SignInView} layout={MinimalLayout} path="/login" />}
          />
          <Route path="reset-password/:code" element={<RouteWithLayout component={ResetPasswordPage} />} />
          <Route path="definir-senha" element={<RouteWithLayout component={SetPasswordPage} />} />
          <Route path="reativar" element={<RouteWithLayout component={StripePremiumWithUserCodeView} />} />
          <Route path="ativar" element={<RouteWithLayout component={StripePremiumWithUserCodeView} />} />
          <Route
            path="reativado"
            element={<RouteWithLayout component={StripePremiumSuccessWithUserCodeView} />}
          />
          <Route path="asaas" element={<RouteWithLayout component={AsaasPremiumWithUserCodeView} />} />
          <Route path="asaas-ativar" element={<RouteWithLayout component={AsaasAtivarWithUserCodeView} />} />
          <Route path="stripe" element={<RouteWithLayout component={StripePortalWithUserCodeView} />} />
          <Route
            path="mudar-data-assinatura"
            element={<RouteWithLayout component={StripeChangeDueDateWithUserCodeView} />}
          />
          <Route
            path="mudar-data-assinatura-sucesso"
            element={<RouteWithLayout component={StripeChangeDueDateSuccessWithUserCodeView} />}
          />
          <Route path="upgrade" element={<RouteWithLayout component={StripeUpgradeWithUserCodeView} />} />
          <Route
            path="upgrade-sucesso"
            element={<RouteWithLayout component={StripeUpgradeSuccessWithUserCodeView} />}
          />
        </Route>
        {/* END - Routes with MinimalLayout */}

        <Route element={<PrintLayout />}>
          <Route
            path="imprimir-pagamento-comissoes"
            element={<RouteWithLayout component={ProductsAndStockBasePage} />}
          >
            <Route path=":id" element={<CommissionPaymentPrintPage />} />
            <Route path=":id/:resumido" element={<CommissionPaymentPrintPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/not-found" />} />
      </Routes>
    </React.Suspense>
  );
};

export default AppRoutes;
